import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiTypography from "components/base/UiTypography";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import {
  ProductMeasurementMaterial,
  ProductType,
  ProposalPlantProduct,
  ProposalTransferTruck,
} from "generated/graphql";
import useUpdateProposalPlantProduct from "hooks/proposals/useUpdateProposalPlantProduct";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { useCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import { capitalCase, sentenceCase, snakeCase } from "change-case";
import PlantOptions from "modules/plants/PlantOptions";
import { draftColors } from "./draftDesignTokens";
import { NUMERIC_SET_VALUE_AS } from "./formUtils";
import { computeTonsPerRound } from "./truckingUtils";
import pluralMeasurementMaterial from "constants/pluralMeasurementMaterial";

type PlantProductPanelProps = {
  pp: ProposalPlantProduct;
  index: number;
  productId: string;
  onDelete?: (plantProductId: string) => void;
  measurementMaterial?: string;
  productType?: string;
  measurementJob?: string;
  hideDepth?: boolean;
};

const labelColor = draftColors.textLabel;
const metricLabelSx = { color: labelColor } as const;
const truckGridTemplate = "3fr 1fr 1fr 1fr 1fr";

const dropdownFieldSx = {
  border: `1px solid ${draftColors.border}`,
  borderRadius: "8px",
  p: "6px 8px 8px",
  "& .MuiInput-root, & .MuiOutlinedInput-root": {
    fontSize: "12px",
    fontWeight: 400,
    color: draftColors.textPrimary,
    borderRadius: "6px",
    border: `1px solid ${draftColors.borderInput}`,
    padding: "4px 8px !important",
    "&::before, &::after": { display: "none" },
  },
  "& .MuiInputBase-input": {
    padding: "2px 0 !important",
    fontSize: "12px",
  },
} as const;

export default function PlantProductPanel({
  pp,
  index,
  productId,
  onDelete,
  measurementMaterial,
  productType,
  measurementJob,
  hideDepth,
}: PlantProductPanelProps) {
  const { control, setValue, register } = useFormContext<UpdateProposalProductInputSchema>();
  const [updateProposalPlantProduct] = useUpdateProposalPlantProduct();
  const [selectedPlantId, setSelectedPlantId] = useState<string>(pp.plant?.plantId ?? "");

  const ppTrucks: ProposalTransferTruck[] = pp.transferTrucks ?? [];

  const plantTruckingCost = ppTrucks.reduce((sum, t) => sum + (t.costOverride ?? t.cost ?? 0), 0);
  const plantMaterialCost = pp.costMaterial ?? 0;
  const totalTons = pp.materialNeededOverride ?? pp.materialNeeded ?? 0;
  const haulingHrs = ppTrucks.reduce((sum, t) => sum + (t.totalHaulingHours ?? 0), 0).toFixed(2);
  const tonsPerRound = computeTonsPerRound(ppTrucks);

  const handlePlantProductChange = useCallback(
    async (value: string | number) => {
      if (!value) return;
      const stringValue = String(value);
      setValue(`plantProducts.${index}.plantProductId` as any, stringValue, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      await updateProposalPlantProduct({
        variables: {
          plantProductId: stringValue,
          proposalProductId: productId,
          proposalPlantProductId: pp.id,
        },
        refetchQueries: ["customGetProposal"],
        awaitRefetchQueries: true,
      });
    },
    [index, setValue, updateProposalPlantProduct, productId, pp.id]
  );

  const materialUnitLabel = pluralMeasurementMaterial[measurementMaterial] ?? "Tons";

  const depthLabel = (() => {
    const type = productType ?? "";
    if (
      type === snakeCase(ProductType.SLURRY_SEAL) ||
      type === snakeCase(ProductType.MICROSURFACING)
    ) {
      return `Pounds per ${measurementJob ? capitalCase(measurementJob) : "Unit"}`;
    }
    return "Depth";
  })();

  const truckingAndMaterialCost = plantTruckingCost + plantMaterialCost;

  return (
    <MDBox
      sx={{
        border: "1px solid rgb(153, 160, 173)",
        borderRadius: "8px",
        backgroundColor: draftColors.surface,
        overflow: "hidden",
      }}
    >
      <MDBox sx={{ px: "12px", py: "12px" }}>
        <MDBox
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr 1fr 1fr 1fr 32px"
          gap="12px"
          sx={{
            backgroundColor: draftColors.surfaceMuted,
            borderRadius: "6px",
            px: "12px",
            py: "8px",
            mb: "8px",
          }}
          className="tour-material-cost-summary"
        >
          {[
            { label: `Total ${materialUnitLabel}`, value: Number(totalTons).toFixed(2) },
            { label: "Hauling Hrs", value: haulingHrs },
            { label: `${materialUnitLabel} Per Round`, value: tonsPerRound.toFixed(2) },
            { label: "Trucking Cost", value: formatCentsToUSD(plantTruckingCost) },
            { label: "Material Cost", value: formatCentsToUSD(plantMaterialCost) },
            { label: "Trucking & Material Cost", value: formatCentsToUSD(truckingAndMaterialCost) },
          ].map((item) => (
            <MDBox key={item.label} display="flex" flexDirection="column" gap="2px">
              <UiTypography uiVariant="label-xs" sx={metricLabelSx}>
                {item.label}
              </UiTypography>
              <UiTypography uiVariant="body-sm">{item.value}</UiTypography>
            </MDBox>
          ))}
          <MDBox
            className="tour-plant-product-delete"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: draftColors.red,
              cursor: "pointer",
            }}
            onClick={() => onDelete?.(pp.id)}
          >
            <Icon fontSize="small">delete</Icon>
          </MDBox>
        </MDBox>

        <PlantOptions>
          {({ plantOptions, plantProductOptions, loading: plantsLoading, search, pagination }) => (
            <MDBox display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr 1fr" gap="8px">
              <MDBox className="tour-plant-dropdown" sx={dropdownFieldSx}>
                <UiTypography uiVariant="label-xs" sx={{ mb: "4px", color: labelColor }}>
                  Plant
                </UiTypography>
                <ScrollAsyncAutocomplete
                  options={plantOptions}
                  value={{
                    label:
                      plantOptions.find((x) => x.value === selectedPlantId)?.label ??
                      pp.plant?.name ??
                      "",
                    value: selectedPlantId,
                  }}
                  onChange={(value) => {
                    const newPlantId = String(value);
                    if (newPlantId !== selectedPlantId) {
                      setSelectedPlantId(newPlantId);
                      setValue(`plantProducts.${index}.plantProductId` as any, "", {
                        shouldDirty: false,
                        shouldTouch: false,
                        shouldValidate: false,
                      });
                    }
                  }}
                  loading={plantsLoading}
                  label=""
                  search={search}
                  onLoadMore={pagination.loadMore}
                  hasMore={pagination.hasNextPage}
                  variant="standard"
                />
              </MDBox>
              <MDBox className="tour-plant-material-dropdown" sx={dropdownFieldSx}>
                <UiTypography uiVariant="label-xs" sx={{ mb: "4px", color: labelColor }}>
                  Plant Material
                </UiTypography>
                <Controller
                  key={`${pp.id}-${pp.plantProductId}`}
                  name={`plantProducts.${index}.plantProductId` as any}
                  control={control}
                  defaultValue={pp.plantProductId}
                  render={({ field }) => (
                    <ScrollAsyncAutocomplete
                      options={(plantProductOptions as any)[selectedPlantId] || []}
                      value={{
                        label:
                          ((plantProductOptions as any)[selectedPlantId] || []).find(
                            (x: any) => x.value === field.value
                          )?.label ??
                          (field.value === pp.plantProductId && pp.type
                            ? sentenceCase(pp.type)
                            : ""),
                        value: field.value ?? "",
                      }}
                      onChange={async (value) => {
                        await handlePlantProductChange(value);
                      }}
                      loading={plantsLoading}
                      label=""
                      search={() => {}}
                      onLoadMore={() => {}}
                      hasMore={false}
                      variant="standard"
                    />
                  )}
                />
              </MDBox>
              {!hideDepth && (
                <UiFieldGroup
                  className="tour-depth"
                  label={depthLabel}
                  value=""
                  placeholder={String(pp.applicationRate ?? "")}
                  fontWeight={500}
                  inputProps={register(
                    `plantProducts.${index}.applicationRateOverride` as any,
                    NUMERIC_SET_VALUE_AS
                  )}
                />
              )}
              <UiFieldGroup
                className="tour-tons"
                label={materialUnitLabel}
                value=""
                placeholder={parseFloat((pp.materialNeeded ?? 0).toFixed(2)).toString()}
                fontWeight={500}
                inputProps={register(
                  `plantProducts.${index}.materialNeededOverride` as any,
                  NUMERIC_SET_VALUE_AS
                )}
              />
              <UiFieldGroup
                className="tour-material-cost"
                label="Cost"
                value=""
                placeholder={String((pp.rate ?? 0) / 100)}
                fontWeight={500}
                startAdornment="$"
                inputProps={register(
                  `plantProducts.${index}.rateOverride` as any,
                  NUMERIC_SET_VALUE_AS
                )}
              />
            </MDBox>
          )}
        </PlantOptions>
      </MDBox>

      {ppTrucks.length > 0 && (
        <>
          <MDBox
            display="grid"
            gridTemplateColumns={truckGridTemplate}
            alignItems="center"
            sx={{
              borderTop: `1px solid ${draftColors.border}`,
              borderBottom: `1px solid ${draftColors.border}`,
              backgroundColor: draftColors.surfaceTableHeader,
              py: "4px",
            }}
          >
            <UiTypography uiVariant="overline" sx={{ px: "8px" }}>
              Truck Name
            </UiTypography>
            {["Rate", "Trips", "Hauling Hours", "Total"].map((header) => (
              <UiTypography
                key={header}
                uiVariant="overline"
                sx={{ px: "8px", textAlign: "right" }}
              >
                {header}
              </UiTypography>
            ))}
          </MDBox>

          {ppTrucks.map((truck: ProposalTransferTruck, idx: number) => (
            <MDBox
              key={truck.id ?? idx}
              display="grid"
              gridTemplateColumns={truckGridTemplate}
              alignItems="center"
              sx={{ borderBottom: `1px solid ${draftColors.border}`, py: "4px" }}
            >
              <UiTypography uiVariant="body-sm" sx={{ px: "8px" }}>
                {truck.name ?? "Transfer Truck"}
              </UiTypography>
              <UiTypography uiVariant="body-sm" sx={{ px: "8px", textAlign: "right" }}>
                {formatCentsToUSD(truck.rateHourlyOverride ?? truck.rateHourly ?? 0)}
              </UiTypography>
              <UiTypography uiVariant="body-sm" sx={{ px: "8px", textAlign: "right" }}>
                {String(truck.quantity ?? 0)}
              </UiTypography>
              <UiTypography uiVariant="body-sm" sx={{ px: "8px", textAlign: "right" }}>
                {(+(truck.totalHaulingHours ?? 0)).toFixed(2)}
              </UiTypography>
              <UiTypography uiVariant="body-sm" sx={{ px: "8px", textAlign: "right" }}>
                {formatCentsToUSD(truck.costOverride ?? truck.cost ?? 0)}
              </UiTypography>
            </MDBox>
          ))}
        </>
      )}
    </MDBox>
  );
}
